import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ErrorMsg, Loading, Modal } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type { ItemDetail, Layout, MotionStart, PublicationRow, Revision, VideoFormat, VideoOptions } from '../../lib/contentTypes';
import { ensureFonts } from './fonts';
import { MotionPreview, MusicUpload, musicPreviewUrls, type PreviewShot } from './MotionPreview';
import { canvasBlob, renderArtboard, type RenderEnv } from './render';

// A motion version of an approved item (docs/PLAN-mirage-video-integration.md section 7): the server
// says which text layers move; each artboard of the APPROVED revision is drawn here by the one renderer
// without them, centred on a canvas of the film's aspect, and sent as one PNG per request. Mirage Video
// moves the camera over each slide and reveals the texts.

const FORMAT_LABEL: Record<VideoFormat, string> = {
  '9:16': '9:16 short / reel / story',
  '4:5': '4:5 feed post',
  '1:1': '1:1 square',
  '16:9': '16:9 film',
};
const REVEAL_LABEL: Record<string, string> = { FADE: 'fade', RISE: 'rise', LETTERS: 'letter by letter', WIPE: 'wipe', MASK: 'mask', TRACKING: 'tracking' };
// Plain words for Mirage Video's camera moves (its own labels are for its editor).
const MOVE_LABEL: Record<string, string> = {
  STATIC: 'Still',
  PUSH_IN: 'Zoom in',
  PULL_OUT: 'Zoom out',
  PAN_LEFT: 'Pan left',
  PAN_RIGHT: 'Pan right',
  RISE: 'Rise',
  LOWER: 'Lower',
  ORBIT_LEFT: 'Orbit left',
  ORBIT_RIGHT: 'Orbit right',
};
const INTENSITY_LABEL: Record<string, string> = { SUBTLE: 'subtle', NORMAL: 'normal', STRONG: 'strong' };
const NONE = 'NONE';

/** What the server says about the item before anything is made (GET motion-plan). */
interface MotionPlan {
  revision_id: string;
  canvas: [number, number];
  slides: { artboard_id: string; focus_x: number; focus_y: number; texts: { layer_id: string; text: string; style: string }[] }[];
}
/** One slide's own choices; null = follow the dialog's global value (focus: the server's default). */
interface Row {
  movement: string | null;
  intensity: string | null;
  seconds: string | null;
  fx: number | null;
  fy: number | null;
}
const EMPTY_ROW: Row = { movement: null, intensity: null, seconds: null, fx: null, fy: null };

/** One slide as the film frame: background colour, the artboard centred, moving texts left out. */
export function motionFrameCanvas(layout: Layout, index: number, moving: Set<string>, canvas: [number, number], env: RenderEnv): HTMLCanvasElement {
  const [w, h] = canvas;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('canvas 2D is not available in this browser');
  const ab = layout.artboards[index];
  ctx.fillStyle = ab.background;
  ctx.fillRect(0, 0, w, h);
  const s = Math.min(w / layout.width, h / layout.height);
  ctx.translate((w - layout.width * s) / 2, (h - layout.height * s) / 2);
  ctx.scale(s, s);
  // What overflows the artboard is cut at its edge, as it is in the still.
  ctx.beginPath();
  ctx.rect(0, 0, layout.width, layout.height);
  ctx.clip();
  const warnings = renderArtboard(ctx, layout, { ...ab, layers: ab.layers.filter((l) => !moving.has(l.id)) }, index, env);
  const stop = warnings.filter((x) => x.blocking);
  if (stop.length) throw new Error(`slide ${index + 1}: ${stop.map((x) => x.message).join('; ')}`);
  return c;
}

export async function motionFrame(layout: Layout, index: number, moving: Set<string>, canvas: [number, number], env: RenderEnv): Promise<Blob> {
  return canvasBlob(motionFrameCanvas(layout, index, moving, canvas, env), 'png');
}

/** A slide's film frame as a small picture; clicking it sets the camera's focus point there. */
function SlideThumb({ layout, index, canvas, env, label, fx, fy, onPick }: { layout: Layout; index: number; canvas: [number, number]; env: RenderEnv; label: string; fx: number; fy: number; onPick: (x: number, y: number) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState('');
  useEffect(() => {
    try {
      const src = motionFrameCanvas(layout, index, new Set(), fitSize(canvas, 160), env);
      const c = ref.current;
      if (!c) return;
      c.width = src.width;
      c.height = src.height;
      c.getContext('2d')?.drawImage(src, 0, 0);
      setFailed('');
    } catch (e) {
      setFailed(errorText(e));
    }
  }, [layout, index, canvas, env]);
  return (
    <button
      type="button"
      className="cs-mthumb"
      aria-label={label}
      title="Click to choose where the camera looks"
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onPick(Math.round(((e.clientX - r.left) / r.width) * 100) / 100, Math.round(((e.clientY - r.top) / r.height) * 100) / 100);
      }}
    >
      <canvas ref={ref} />
      {failed && <span className="small warn-text">{failed}</span>}
      <i className="cs-mfocus" style={{ left: `${fx * 100}%`, top: `${fy * 100}%` }} />
    </button>
  );
}

/** The film canvas scaled so its longer edge is `edge` pixels. */
function fitSize(canvas: [number, number], edge: number): [number, number] {
  const k = Math.min(1, edge / Math.max(canvas[0], canvas[1]));
  return [Math.round(canvas[0] * k), Math.round(canvas[1] * k)];
}

export function MotionDialog({ item, env, onClose }: { item: ItemDetail; env: RenderEnv; onClose: () => void }) {
  const [opts, setOpts] = useState<VideoOptions | null>(null);
  const [pubs, setPubs] = useState<PublicationRow[] | null>(null);
  const [plan, setPlan] = useState<MotionPlan | null>(null);
  const [layout, setLayout] = useState<Layout | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [reveals, setReveals] = useState<Record<string, string>>({}); // text layer id -> reveal | NONE; missing = the global one
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const [done, setDone] = useState<string | null>(null); // the publication the video went to
  const [key] = useState(newIdempotencyKey);
  const [f, setF] = useState({ publication: '', format: '9:16' as VideoFormat, seconds: '3', movement: 'PUSH_IN', reveal: 'RISE', transition: 'CROSSFADE', music: '', logo: '' });

  useEffect(() => {
    if (!item.project_id) return;
    Promise.all([api<VideoOptions>('/api/content/videos/options'), api<{ items: PublicationRow[] }>(`/api/content/publications?project_id=${item.project_id}`)])
      .then(([o, p]) => {
        setOpts(o);
        setPubs(p.items);
        const video = p.items.find((x) => ['short', 'reel', 'story', 'video'].includes(x.kind));
        setF((x) => ({
          ...x,
          publication: (video ?? p.items[0])?.id ?? '',
          format: o.defaults.format,
          seconds: String(o.defaults.duration_sec),
          movement: o.defaults.movement,
          transition: o.defaults.transition,
          reveal: o.text_reveals.includes('RISE') ? 'RISE' : (o.text_reveals[0] ?? 'FADE'),
          logo: o.default_logo_id ?? '',
          music: o.music[0]?.id ?? '',
        }));
      })
      .catch((e) => setError(errorText(e)));
  }, [item.project_id]);

  // Which texts move and where the camera looks by default: the server decides, per format.
  useEffect(() => {
    let live = true;
    api<MotionPlan>(`/api/content-items/${item.id}/motion-plan?format=${encodeURIComponent(f.format)}`)
      .then((p) => {
        if (!live) return;
        setPlan(p);
        setRows((old) => p.slides.map((_, i) => old[i] ?? EMPTY_ROW));
      })
      .catch((e) => live && setError(errorText(e)));
    return () => {
      live = false;
    };
  }, [item.id, f.format]);

  // The approved revision's layout (the item may have moved on): thumbnails and preview draw from it.
  useEffect(() => {
    if (!plan) return;
    let live = true;
    (async () => {
      let l = item.revision?.id === plan.revision_id ? item.revision.layout : undefined;
      if (!l && item.approval) l = (await api<Revision & { layout: Layout }>(`/api/content-items/${item.id}/revisions/${item.approval.revision_number}`)).layout;
      if (!l) throw new Error('the approved revision could not be loaded');
      await ensureFonts();
      if (live) setLayout(l);
    })().catch((e) => live && setError(errorText(e)));
    return () => {
      live = false;
    };
  }, [plan?.revision_id, item.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const slides = plan?.slides.length ?? item.revision?.layout.artboards.length ?? 0;
  const seconds = Number(f.seconds);
  const eff = (i: number) => ({
    movement: rows[i]?.movement ?? f.movement,
    intensity: rows[i]?.intensity ?? opts?.defaults.intensity ?? 'SUBTLE',
    seconds: rows[i]?.seconds ?? f.seconds,
  });
  const rowSeconds = Array.from({ length: slides }, (_, i) => Number(eff(i).seconds));
  const badSeconds = !(seconds >= 1 && seconds <= 20) || rowSeconds.some((x) => !(x >= 1 && x <= 20));
  const totalSeconds = rowSeconds.reduce((n, x) => n + x, 0);
  const setRow = (i: number, patch: Partial<Row>) => setRows((all) => all.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const canMoveTexts = (opts?.text_reveals.length ?? 0) > 0;

  const previewShots = useMemo<PreviewShot[]>(() => {
    if (!plan || !opts) return [];
    return plan.slides.map((sl, i) => {
      const r = rows[i] ?? EMPTY_ROW;
      return {
        movement: r.movement ?? f.movement,
        intensity: r.intensity ?? opts.defaults.intensity,
        seconds: Math.min(20, Math.max(1, Number(r.seconds ?? f.seconds) || 1)),
        fx: r.fx ?? sl.focus_x,
        fy: r.fy ?? sl.focus_y,
        texts: canMoveTexts ? sl.texts.map((t) => ({ layerId: t.layer_id, reveal: reveals[t.layer_id] || f.reveal })) : [],
      };
    });
  }, [plan, opts, rows, reveals, f.movement, f.seconds, f.reveal, canMoveTexts]);
  const previewSize = useMemo(() => (plan ? fitSize(plan.canvas, 560) : ([315, 560] as [number, number])), [plan]);
  const musicUrl = f.music ? (musicPreviewUrls.get(f.music) ?? null) : null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!opts || !item.approval) return;
    setError('');
    try {
      setProgress('Checking the item…');
      const m = await api<MotionStart>(`/api/content-items/${item.id}/motion`, {
        method: 'POST',
        idempotencyKey: key,
        body: {
          publication_id: f.publication,
          format: f.format,
          duration_sec: seconds,
          movement: f.movement,
          intensity: opts.defaults.intensity,
          pace: opts.defaults.pace,
          transition: f.transition,
          reveal: f.reveal,
          music_library_id: f.music || null,
          logo_asset_id: f.logo || null,
          shots: Array.from({ length: slides }, (_, i) => {
            const r = rows[i] ?? EMPTY_ROW;
            const e1 = eff(i);
            return { movement: e1.movement, intensity: e1.intensity, duration_sec: Number(e1.seconds), ...(r.fx != null && r.fy != null ? { focus_x: r.fx, focus_y: r.fy } : {}) };
          }),
          reveals: Object.fromEntries(Object.entries(reveals).filter(([, v]) => v)),
        },
      });
      // The frames come from the revision the server bound the motion version to (the approved one).
      let l = item.revision?.layout;
      if (item.revision?.id !== m.revision_id) {
        l = (await api<Revision & { layout: Layout }>(`/api/content-items/${item.id}/revisions/${item.approval.revision_number}`)).layout;
      }
      if (!l) throw new Error('the approved revision could not be loaded');
      await ensureFonts();
      for (let i = 0; i < m.slides.length; i++) {
        setProgress(`Drawing and sending slide ${i + 1} of ${m.slides.length}…`);
        const blob = await motionFrame(l, i, new Set(m.slides[i].moving_layers), m.canvas, env);
        await api(`/api/content/videos/${m.video.id}/frames/${i}`, { method: 'PUT', raw: blob });
      }
      setProgress('Starting…');
      await api(`/api/content/videos/${m.video.id}/start`, { method: 'POST' });
      setProgress('');
      setDone(f.publication);
    } catch (err) {
      setProgress('');
      setError(errorText(err));
    }
  }

  const sending = !!progress;
  const revealWord = (r: string) => REVEAL_LABEL[r] ?? r.toLowerCase();
  return (
    <Modal
      title="Make a motion version in Mirage Video"
      onClose={onClose}
      wide
      footer={
        done ? (
          <Link className="btn primary" to={`/content/publications/${done}#videos`}>
            Open the publication
          </Link>
        ) : (
          <button type="submit" form="make-motion" className="btn primary" disabled={!opts || !plan || sending || !f.publication || badSeconds}>
            {sending ? 'Sending…' : `Create the motion version (${slides} slide${slides === 1 ? '' : 's'}, ${badSeconds ? '?' : totalSeconds.toFixed(1)} s)`}
          </button>
        )
      }
    >
      {done ? (
        <p role="status">The motion version is being created in Mirage Video. It appears in the publication's Videos section; its film will come back into the publication's files.</p>
      ) : !opts || !pubs ? (
        error ? <ErrorMsg>{error}</ErrorMsg> : <Loading what="Asking Mirage Video for its options" />
      ) : (
        <form id="make-motion" className="stack" onSubmit={submit} aria-label="Make a motion version">
          <p className="muted small">
            Each slide of the approved revision {item.approval?.revision_number} becomes a shot with a slow camera move; its titles, labels and captions appear with an animation in their own colour; longer paragraphs stay in the picture. The still item and its approval are not changed.
          </p>
          {pubs.length === 0 ? (
            <p className="msg warn">This project has no publication yet: create one (a short or a reel) on the project's page first; the film comes back into its files.</p>
          ) : (
            <label className="field">
              <span>Publication (the film comes back into its files)</span>
              <select value={f.publication} onChange={(e) => setF({ ...f, publication: e.target.value })} aria-label="Publication">
                {pubs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} ({p.kind})
                  </option>
                ))}
              </select>
            </label>
          )}
          <fieldset className="field">
            <legend>Format</legend>
            <div className="row gap wrap">
              {opts.formats.map((x) => (
                <label key={x} className="check">
                  <input type="radio" name="motion-format" value={x} checked={f.format === x} onChange={() => setF({ ...f, format: x })} /> {FORMAT_LABEL[x]}
                </label>
              ))}
            </div>
          </fieldset>
          <p className="muted small">For all slides (each slide below can differ):</p>
          <div className="form-grid">
            <label className="field">
              <span>Seconds per slide</span>
              <input type="number" min={1} max={20} step={0.5} value={f.seconds} onChange={(e) => setF({ ...f, seconds: e.target.value })} aria-label="Seconds per slide" />
            </label>
            <label className="field">
              <span>Camera move</span>
              <select value={f.movement} onChange={(e) => setF({ ...f, movement: e.target.value })} aria-label="Camera move">
                {opts.movements.map((m) => (
                  <option key={m.key} value={m.key}>
                    {MOVE_LABEL[m.key] ?? m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Text animation</span>
              <select value={f.reveal} onChange={(e) => setF({ ...f, reveal: e.target.value })} aria-label="Text animation">
                {(opts.text_reveals.length ? opts.text_reveals : ['FADE']).map((r) => (
                  <option key={r} value={r}>
                    {revealWord(r)}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Between slides</span>
              <select value={f.transition} onChange={(e) => setF({ ...f, transition: e.target.value })}>
                {opts.transitions.map((t) => (
                  <option key={t} value={t}>
                    {t.replace('_', ' ').toLowerCase()}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Music (from Mirage Video's library)</span>
              <select value={f.music} onChange={(e) => setF({ ...f, music: e.target.value })} aria-label="Music">
                <option value="">no music</option>
                {opts.music.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label || m.original_name}
                    {m.duration_sec ? ` (${Math.round(m.duration_sec)} s)` : ''}
                    {m.credit ? ' · credit required' : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Logo at the end (brand kit)</span>
              <select value={f.logo} onChange={(e) => setF({ ...f, logo: e.target.value })}>
                <option value="">no logo</option>
                {opts.logos.map((l) => (
                  <option key={l.asset_id} value={l.asset_id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <MusicUpload
            onUploaded={(t) => {
              setOpts((o) => (o ? { ...o, music: o.music.some((m) => m.id === t.id) ? o.music : [...o.music, t] } : o));
              setF((x) => ({ ...x, music: t.id }));
            }}
          />
          <h3 className="small">Slide by slide</h3>
          {!plan || !layout ? (
            <Loading what="Reading the slides" />
          ) : (
            <ol className="cs-mslides">
              {plan.slides.map((sl, i) => {
                const r = rows[i] ?? EMPTY_ROW;
                const e1 = eff(i);
                const fx = r.fx ?? sl.focus_x;
                const fy = r.fy ?? sl.focus_y;
                return (
                  <li key={sl.artboard_id} className="cs-mslide" data-slide={i + 1}>
                    <SlideThumb layout={layout} index={i} canvas={plan.canvas} env={env} label={`Focus point of slide ${i + 1}`} fx={fx} fy={fy} onPick={(x, y) => setRow(i, { fx: x, fy: y })} />
                    <div className="cs-mslide-fields">
                      <strong className="small">Slide {i + 1}</strong>
                      <label className="field">
                        <span>Movement</span>
                        <select value={e1.movement} onChange={(e) => setRow(i, { movement: e.target.value })} aria-label={`Slide ${i + 1} movement`}>
                          {opts.movements.map((m) => (
                            <option key={m.key} value={m.key}>
                              {MOVE_LABEL[m.key] ?? m.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="field">
                        <span>Strength</span>
                        <select value={e1.intensity} onChange={(e) => setRow(i, { intensity: e.target.value })} aria-label={`Slide ${i + 1} strength`}>
                          {opts.intensities.map((x) => (
                            <option key={x} value={x}>
                              {INTENSITY_LABEL[x] ?? x.toLowerCase()}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="field">
                        <span>Seconds</span>
                        <input type="number" min={1} max={20} step={0.5} value={e1.seconds} onChange={(e) => setRow(i, { seconds: e.target.value })} aria-label={`Slide ${i + 1} seconds`} />
                      </label>
                      <p className="small muted">
                        Camera looks at {Math.round(fx * 100)} % across, {Math.round(fy * 100)} % down{r.fx == null ? ' (the slide\'s main picture)' : ''}.{' '}
                        {r.fx != null && (
                          <button type="button" className="btn small ghost" onClick={() => setRow(i, { fx: null, fy: null })}>
                            Reset focus
                          </button>
                        )}
                      </p>
                      {canMoveTexts &&
                        sl.texts.map((t, j) => (
                          <label key={t.layer_id} className="field cs-mtext">
                            <span>“{t.text.length > 40 ? `${t.text.slice(0, 40)}…` : t.text}”</span>
                            <select value={reveals[t.layer_id] ?? ''} onChange={(e) => setReveals((all) => ({ ...all, [t.layer_id]: e.target.value }))} aria-label={`Slide ${i + 1} text ${j + 1} reveal`}>
                              <option value="">same as all texts ({revealWord(f.reveal)})</option>
                              {opts.text_reveals.map((x) => (
                                <option key={x} value={x}>
                                  {revealWord(x)}
                                </option>
                              ))}
                              <option value={NONE}>stays in the picture (no animation)</option>
                            </select>
                          </label>
                        ))}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
          <div className="row gap">
            <button type="button" className="btn" disabled={!plan || !layout} onClick={() => setShowPreview((v) => !v)} aria-expanded={showPreview}>
              {showPreview ? 'Hide the preview' : 'Preview'}
            </button>
          </div>
          {showPreview && layout && plan && (
            <MotionPreview
              layout={layout}
              size={previewSize}
              frames={(i, moving, size) => motionFrameCanvas(layout, i, moving, size, env)}
              shots={previewShots}
              transition={f.transition}
              pace={opts.defaults.pace}
              musicUrl={musicUrl}
              onError={setError}
            />
          )}
          {badSeconds && <p className="msg warn">A slide lasts between 1 and 20 seconds.</p>}
          {opts.text_reveals.length === 0 && <p className="msg warn">This Mirage Video release does not take animated texts yet: the texts stay in the pictures.</p>}
          {opts.style_notes.length > 0 && <p className="small warn-text">{opts.style_notes.join(' · ')}</p>}
          {progress && (
            <p className="small" role="status">
              {progress}
            </p>
          )}
          <ErrorMsg>{error}</ErrorMsg>
        </form>
      )}
    </Modal>
  );
}
